import React, { useState } from 'react';
import { Label } from 'reactstrap';
import classnames from 'classnames';
import FilterableSelect from '../../../../../../common/components/FilterableSelect/FilterableSelect';
import {
  generateBasicStructure,
  nameById,
} from '@mm/state/helpers/filterable-select-helpers';
import ImageUploader from '../../../../../../common/components/ImageUploader/ImageUploader';
import VideoUploader from '../VideoUploader/VideoUploader';
import { DEFAULT_NAME } from '../../../constants/constants';
import { ErrorTooltip } from '../../../../../../common/components/ErrorTooltip/ErrorTooltip';
import { InputWithEmoji } from '../../../../../../common/components/InputWithEmoji/InputWithEmoji';
import Hint from '../../../../../../common/components/Hint/Hint';
import { CallToAction } from '@mm/state/interfaces/adAccounts';
import { AdModel } from '@mm/state/model/AdModel';
import { adCloning } from '../utils';
import AdConfigModel from '@mm/state/model/AdConfigModel';
import SelectorWithArrow from '../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { AdPreview } from './AdPreview';
import { InfoCircleFilled } from '@ant-design/icons';
import { InputMaxLength } from '@mm/components/src/InputMaxLength/InputMaxLength';

type Props = {
  // utility values
  adConfig: AdConfigModel | null;
  showAdsSpinner: boolean;
  isVideoMedia: boolean;
  usedInSlideout?: boolean;
  // collections
  ads: AdModel[];
  // fields
  sourceAdId: string;
  creativeText: string;
  creativeHeadline: string;
  creativeDescription: string;
  imageAssetId: number | null | string;
  thumbAssetId: number | null;
  imageUrl: string | null;
  videoUrl: string | null;
  thumbUrl: string | null;
  callToAction: CallToAction;
  // callbacks
  onSelectCloneAd: () => void;
  onSelectNewAd: () => void;
  onAdChange: (id: number) => void;
  onSelectImageMedia: () => void;
  onSelectVideoMedia: () => void;
  onImageUpload: (assetId: number, url: string) => void;
  onVideoUpload: (assetId: number, url: string) => void;
  onThumbUpload: (assetId: number, url: string) => void;
  onChangeCreativeText: (text: string) => void;
  onChangeCreativeHeadline: (text: string) => void;
  onChangeCreativeDescription: (text: string) => void;
  onBlurCreativeText: () => void;
  onBlurCreativeHeadline: () => void;
  onBlurCreativeDescription: () => void;
  onChangeCallToAction: (id: CallToAction) => void;
  // validations
  assetIdValidations?: string[];
  thumbIdValidations?: string[];
  creativeTextValidations?: string[];
  creativeHeadlineValidations?: string[];
  creativeDescriptionValidations?: string[];
};

const callToActions = [
  { id: CallToAction.SEND_MESSAGE, name: 'Send message' },
  { id: CallToAction.APPLY_NOW, name: 'Apply now' },
  { id: CallToAction.CONTACT_US, name: 'Contact us' },
  { id: CallToAction.DONATE_NOW, name: 'Donate now' },
  { id: CallToAction.GET_QUOTE, name: 'Get quote' },
  { id: CallToAction.LEARN_MORE, name: 'Learn more' },
  { id: CallToAction.SHOP_NOW, name: 'Shop now' },
  { id: CallToAction.SIGN_UP, name: 'Sign up' },
  { id: CallToAction.SUBSCRPIBE, name: 'Subscribe' },
  { id: CallToAction.BOOK_NOW, name: 'Book Now' },
];

export const AdCreativeStep = (props: Props) => {
  const {
    adConfig,
    ads,
    sourceAdId,
    creativeText,
    creativeHeadline,
    creativeDescription,
    showAdsSpinner,
    isVideoMedia,
    usedInSlideout,
    videoUrl,
    imageUrl,
    thumbUrl,
    imageAssetId,
    thumbAssetId,
    callToAction,
    onSelectCloneAd,
    onSelectNewAd,
    onAdChange,
    onSelectImageMedia,
    onSelectVideoMedia,
    onImageUpload,
    onVideoUpload,
    onThumbUpload,
    onChangeCreativeText,
    onChangeCreativeHeadline,
    onChangeCreativeDescription,
    onBlurCreativeText,
    onBlurCreativeHeadline,
    onBlurCreativeDescription,
    onChangeCallToAction,
    assetIdValidations,
    thumbIdValidations,
    creativeTextValidations,
    creativeHeadlineValidations,
    creativeDescriptionValidations,
  } = props;
  const cloneAdSelected = adCloning(adConfig);
  const draft = adConfig?.draft;
  const inProgress = adConfig?.publishingProcess;
  const showRestrictionMessage =
    adConfig?.sourceAdId && !(isVideoMedia ? videoUrl : imageUrl);
  const disabled = !adConfig || !draft || inProgress;
  const radioLabelClasses = classnames('ad-detail__radio-label', {
    'ad-detail__radio-label--disabled': disabled,
  });

  return (
    <>
      <div className="ad-config-detail__ad-selection ad-detail__radio-row">
        <div className="ad-config-detail__clone-ad ad-detail__radio-container">
          <input
            type="radio"
            id="clone-ad-checker"
            className="ad-detail__radio-input"
            disabled={ads.length === 0 || inProgress}
            checked={cloneAdSelected}
            onChange={onSelectCloneAd}
          />
          <Label className={radioLabelClasses} for="clone-ad-checker">
            Clone existing
          </Label>
        </div>
        <div className="ad-config-detail__new-ad ad-detail__radio-container">
          <input
            type="radio"
            id="new-ad-checker"
            className="ad-detail__radio-input"
            checked={!cloneAdSelected}
            onChange={onSelectNewAd}
          />
          <Label className={radioLabelClasses} for="new-ad-checker">
            Create new
          </Label>
        </div>
      </div>
      {cloneAdSelected && (
        <div className="ad-detail__inputs-container">
          <div
            className={classnames('ad-detail__input-container', {
              'ad-detail__input-container-slideout': usedInSlideout,
            })}
          >
            <div className="ad-detail__field-title">Select Ad to clone</div>
            <div className="ad-config-detail__ad-set-selector ad-detail__selector">
              <FilterableSelect
                className="ad-config-detail__highlighted-dropdown"
                showSpinner={showAdsSpinner}
                disabled={inProgress}
                dynamicChangeSuggestions={true}
                onChange={onAdChange}
                outerValue={nameById(
                  sourceAdId,
                  ads.map(ad => ({
                    ...ad,
                    id: Number(ad.id),
                  })),
                  adConfig?.sourceAdId
                    ? `Ad ${adConfig.sourceAdId} (Access Restricted)`
                    : 'No Source Ad',
                )}
                suggestions={generateBasicStructure(ads)}
                placeholder="Select Ad"
              />
            </div>
          </div>
        </div>
      )}
      <div className="ad-config-detail__ad-format-choices-wrapper">
        <div className="ad-config-detail__ad-format-choices ad-detail__radio-row">
          <b className="ad-detail__radio-title">Ad Format:</b>
          <div className="ad-config-detail__ad-format-single ad-detail__radio-container">
            <input
              type="radio"
              id="format-checker"
              className="ad-detail__radio-input"
              checked={true}
            />
            <Label className={radioLabelClasses} for="format-checker">
              Single Image/Video
            </Label>
          </div>
        </div>
        <div className="ad-config-detail__media-type ad-detail__radio-row">
          <b className="ad-detail__radio-title">Media Type:</b>
          <div className="ad-config-detail__image-media ad-detail__radio-container">
            <input
              type="radio"
              id="image-checker"
              disabled={inProgress}
              className="ad-detail__radio-input"
              checked={!isVideoMedia}
              onChange={onSelectImageMedia}
            />
            <Label className={radioLabelClasses} for="image-checker">
              Image
            </Label>
          </div>
          <div className="ad-config-detail__video-media ad-detail__radio-container">
            <input
              type="radio"
              id="video-checker"
              disabled={inProgress}
              className="ad-detail__radio-input"
              checked={isVideoMedia}
              onChange={onSelectVideoMedia}
            />
            <Label className={radioLabelClasses} for="video-checker">
              Video
            </Label>
          </div>
        </div>
      </div>
      <div className="ad-config-detail__media">
        <div
          className={classnames('ad-config-detail__media-container', {
            'ad-config-detail__media-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-config-detail__media-title">
            {(isVideoMedia ? 'Video File' : 'Image File') +
              (showRestrictionMessage
                ? ' (Not Shown - Restricted Access)'
                : '')}
          </div>
          <ImageUploader
            className={
              isVideoMedia
                ? 'ad-config-detail__hidden-uploader'
                : 'ad-config-detail__media-uploader'
            }
            needsFileModification={Boolean(imageAssetId)}
            file={imageUrl}
            disabled={inProgress}
            onUpload={onImageUpload}
          />
          <VideoUploader
            className={
              isVideoMedia
                ? 'ad-config-detail__media-uploader'
                : 'ad-config-detail__hidden-uploader'
            }
            file={videoUrl}
            disabled={inProgress}
            onUpload={onVideoUpload}
            description={(adConfig && adConfig.name) || DEFAULT_NAME}
          />
          <ErrorTooltip
            classPrefix="ad-detail"
            validation={assetIdValidations}
            hoverMode
          />
        </div>
        {isVideoMedia && (
          <div
            className={classnames('ad-config-detail__media-container', {
              'ad-config-detail__media-container-slideout': usedInSlideout,
            })}
          >
            <div className="ad-config-detail__media-title">Video Thumbnail</div>
            <ImageUploader
              className="ad-config-detail__media-uploader"
              file={thumbUrl}
              disabled={inProgress}
              needsFileModification={Boolean(thumbAssetId)}
              onUpload={onThumbUpload}
            />
            <ErrorTooltip
              classPrefix="ad-detail"
              validation={thumbIdValidations}
              hoverMode
            />
          </div>
        )}
        <div
          className={classnames('ad-config-detail__media-preview', {
            'ad-config-detail__media-preview-slideout': usedInSlideout,
          })}
        >
          <div className="ad-config-detail__media-title ad-config-detail__preview-title">
            Preview
          </div>
          <AdPreview
            videoUrl={isVideoMedia ? videoUrl : null}
            imageUrl={isVideoMedia ? null : imageUrl}
            thumbUrl={isVideoMedia ? thumbUrl : null}
            headline={creativeHeadline}
            primaryText={creativeText}
            callToAction={
              callToActions.find(cta => cta.id === callToAction)?.name
            }
          />
        </div>
      </div>
      <div className="ad-detail__inputs-container">
        <div
          className={classnames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-detail__field-title">Primary Text</div>
          <InputMaxLength
            max={125}
            value={creativeText}
            css={{ details: 'ad-config-detail__creative-text-length' }}
          >
            <InputWithEmoji
              multiline
              autosize
              textInputClassName="ad-detail__textarea ad-config-detail__creative-text"
              placeholder="Primary Text"
              disabled={inProgress}
              value={creativeText}
              onChange={onChangeCreativeText}
              onBlur={onBlurCreativeText}
            />
            <ErrorTooltip
              classPrefix="ad-detail"
              validation={creativeTextValidations}
              hoverMode
            />
          </InputMaxLength>
        </div>
      </div>
      <div className="ad-detail__inputs-container">
        <div
          className={classnames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-detail__field-title">Headline</div>
          <InputMaxLength
            max={40}
            value={creativeHeadline}
            css={{ details: 'ad-config-detail__creative-text-length' }}
          >
            <InputWithEmoji
              textInputClassName="ad-detail__input"
              placeholder="Headline"
              disabled={inProgress}
              value={creativeHeadline}
              onChange={onChangeCreativeHeadline}
              onBlur={onBlurCreativeHeadline}
            />
            <ErrorTooltip
              classPrefix="ad-detail"
              validation={creativeHeadlineValidations}
              hoverMode
            />
          </InputMaxLength>
        </div>
      </div>
      <div className="ad-detail__inputs-container">
        <div
          className={classnames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-detail__field-title">Description</div>
          <InputWithEmoji
            multiline
            autosize
            textInputClassName="ad-detail__textarea ad-config-detail__creative-description"
            placeholder="Description"
            disabled={inProgress}
            value={creativeDescription}
            onChange={onChangeCreativeDescription}
            onBlur={onBlurCreativeDescription}
          />
          <ErrorTooltip
            classPrefix="ad-detail"
            validation={creativeDescriptionValidations}
            hoverMode
          />
        </div>
      </div>
      <div className="ad-detail__inputs-container">
        <div
          className={classnames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-detail__field-title">Call-to-Action</div>
          <div className="ad-config-detail__cta-selector ad-detail__selector">
            <SelectorWithArrow
              selectedItem={callToActions.find(
                item => item.id === callToAction,
              )}
              onChange={onChangeCallToAction}
              disabled={disabled}
              items={callToActions}
              arrowColor="#262626"
              arrowSize={14}
            />
          </div>
        </div>
      </div>
    </>
  );
};
